import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetServices } from "../../hooks/services/useGetServices";
import { useDeleteService } from "../../hooks/services/useDeleteService";
import { useDeleteAllServices } from "../../hooks/services/useDeleteAllServices";
import { useRestoreAllServices } from "../../hooks/services/useRestoreAllServices";
import { useForceDeleteAllServices } from "../../hooks/services/useForceDeleteAllServices";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ServicesTableRow from "./Table/ServicesTableRow";
import { ServicesTableColumns } from "./Table/ServicesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const ServicesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("services.view"),
            create: has("services.create"),
            edit: has("services.update"),
            delete: has("services.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deleteServiceMutate, isPending: isDelete } = useDeleteService();
    const { deleteAllServicesMutate, isPending: isDeleteAll } = useDeleteAllServices();
    const { restoreAllServicesMutate, isPending: isRestoreAll } = useRestoreAllServices();
    const { forceDeleteAllServicesMutate, isPending: isForceDeleteAll } = useForceDeleteAllServices();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/services",
        deleteSingle: deleteServiceMutate,
        deleteAll: deleteAllServicesMutate,
        restoreSingle: (id, options) => restoreAllServicesMutate({ items: [id] }, options),
        restoreAll: restoreAllServicesMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllServicesMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllServicesMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            show_in_home: statusFilter || undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter, order]
    );

    const { isLoading, error, services, meta } = useGetServices(params);

    // تجهيز الفلاتر لـ TableLayout
    const filters = useMemo(() => [
        {
            key: "show_in_home",
            label: { ar: "عرض في الرئيسية", en: "Show in Home" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "معروض", en: "Yes" } },
                { value: "0", label: { ar: "مخفي", en: "No" } },
            ],
        },
    ], [statusFilter]);

    const columns = useMemo(() => ServicesTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "الخدمات", en: "Services" }}
            columns={columns}
            items={services}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, services)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/services/create")}
            addButtonLabel={{ ar: "إضافة خدمة", en: "Add Service" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <ServicesTableRow
                    {...params}
                    key={params.item.id}
                    service={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default ServicesTable;